import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, withModifiers as _withModifiers, createBlock as _createBlock, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-4",
  "data-testid": "activity-requests-tab"
}
const _hoisted_2 = {
  class: "flex flex-wrap items-center gap-2",
  "data-testid": "activity-requests-saved-views"
}
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", { class: "text-xs font-bold text-neutral50" }, "Visões salvas", -1)
const _hoisted_4 = ["data-testid", "onClick"]
const _hoisted_5 = {
  key: 0,
  title: "Padrão ao abrir a aba"
}
const _hoisted_6 = ["aria-label", "data-testid", "onClick"]
const _hoisted_7 = { class: "ml-auto flex items-center gap-2" }
const _hoisted_8 = {
  key: 0,
  "data-testid": "activity-requests-export-button"
}
const _hoisted_9 = ["onSubmit"]
const _hoisted_10 = ["maxlength"]
const _hoisted_11 = { class: "flex items-center gap-1 text-xs" }
const _hoisted_12 = /*#__PURE__*/_createElementVNode("span", { class: "text-[11px] text-neutral50" }, " Salva unidades, visão, filtros e ordenação. Não salva a busca. ", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("button", {
  type: "submit",
  class: "rounded-md bg-primary30 px-3 py-1 text-xs font-bold text-white",
  "data-testid": "activity-requests-save-view-submit"
}, " Salvar ", -1)
const _hoisted_14 = {
  key: 3,
  class: "flex flex-col items-center gap-2 py-10 text-center",
  "data-testid": "activity-requests-empty"
}
const _hoisted_15 = { class: "m-0 text-md font-bold text-textPrimary" }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold" }, "Exportar solicitações", -1)
const _hoisted_17 = {
  class: "text-sm",
  "data-testid": "activity-requests-export-confirm"
}
const _hoisted_18 = { class: "flex w-full justify-end gap-2" }
const _hoisted_19 = ["disabled"]

import { computed, onMounted, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'

import Network from '@/Network'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import Modal from '@/components/designSystem/modals/Modal.vue'
import { useServerNowTick } from '@/components/designSystem/QueueViews/useServerNowTick'
import type { ModuleViewCounters } from '@/components/designSystem/QueueViews/types'
import { useErrorToast } from '@/composables/useErrorToast'
import {
  ACTIVITY_REQUEST_ORDER_BY,
  ACTIVITY_REQUEST_ORDER_BY_LABELS,
  ACTIVITY_REQUEST_PAGE_SIZE
} from '@/constants/activity-requests.constants'
import type { ShiftResponse } from '@/Network/Types/Responses/ShiftResponses'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import type {
  ActivityRequestItem,
  ActivityRequestOrderBy,
  ActivityRequestQuery,
  ActivityRequestQuickViewKey
} from '@/types/activity-requests.types'
import { getApiErrorMessage } from '@/utils/apiError'

import ActivityRequestCard from './ActivityRequestCard.vue'
import ActivityRequestDetailDrawer from './ActivityRequestDetailDrawer.vue'
import ActivityRequestFilterBar from './ActivityRequestFilterBar.vue'
import ActivityRequestQuickViews from './ActivityRequestQuickViews.vue'
import {
  applyQuickView,
  hasActiveActivityRequestFilters,
  quickViewOf,
  useActivityRequestFilters
} from '../../composables/useActivityRequestFilters'
import {
  useActivityRequestSavedViews,
  SAVED_VIEW_NAME_MAX_LENGTH
} from '../../composables/useActivityRequestSavedViews'
import type { ActivityRequestSavedView } from '../../composables/useActivityRequestSavedViews'
import { useOpenAttendanceContainer } from '../../composables/useOpenAttendanceContainer'
import {
  buildScheduleOptions,
  buildShiftOptions,
  buildUnitOptions
} from '../../utils/activity-request-filter-options'
import type { FilterOption } from '../../utils/activity-request-filter-options'


export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityRequestsTab',
  props: {
    canExport: { type: Boolean }
  },
  emits: ["counters"],
  setup(__props: any, { emit }) {




const toast = useToast()
const { notifyError } = useErrorToast()
const dsAttendanceStore = useDsAttendanceStore()
const { openContainer } = useOpenAttendanceContainer()

/* ------------------------------------------------------------ filtros e visões */

const savedViews = useActivityRequestSavedViews()
const { filters, page, query, update, setPage, clear, replaceAll, hydrate } =
  useActivityRequestFilters({ initial: () => savedViews.defaultView.value?.query ?? null })

// Hidrata ANTES de observar a query: a primeira carga é a do `onMounted`, não um disparo do watch.
hydrate()

const activeQuickView = computed(() => quickViewOf(filters.value))
const hasActive = computed(() => hasActiveActivityRequestFilters(filters.value))

const SORT_OPTIONS = ACTIVITY_REQUEST_ORDER_BY.map((value) => ({
  label: ACTIVITY_REQUEST_ORDER_BY_LABELS[value],
  value
}))
const isSortOpen = ref(false)

function onSort(value: string): void {
  isSortOpen.value = false
  update({ orderBy: value as ActivityRequestOrderBy }, { keepPage: false })
}

function onQuickView(key: ActivityRequestQuickViewKey): void {
  update(applyQuickView(filters.value, key))
}

const isSaveFormOpen = ref(false)
const newViewName = ref('')
const newViewIsDefault = ref(false)

function submitSaveView(): void {
  const saved = savedViews.saveView(newViewName.value, filters.value, newViewIsDefault.value)
  if (!saved) {
    toast.error('Dê um nome à visão.')
    return
  }
  toast.success(`Visão "${saved.name}" salva.`)
  isSaveFormOpen.value = false
  newViewName.value = ''
  newViewIsDefault.value = false
}

function applySavedView(view: ActivityRequestSavedView): void {
  replaceAll(savedViews.filtersOf(view))
}

/* ------------------------------------------------------------ opções dos filtros */

const groupOptions = ref<FilterOption[]>([])
const shifts = ref<ShiftResponse[]>([])
const shiftOptionsUnavailable = ref(false)

const unitOptions = computed(() => buildUnitOptions(dsAttendanceStore.filterHealthUnits))
const scheduleOptions = computed(() =>
  buildScheduleOptions(
    dsAttendanceStore.filterSchedules,
    dsAttendanceStore.filterHealthUnits,
    filters.value.healthUnitIds,
    filters.value.scheduleIds
  )
)
const shiftOptions = computed(() =>
  buildShiftOptions(
    shifts.value,
    dsAttendanceStore.filterSchedules,
    dsAttendanceStore.filterHealthUnits,
    filters.value.healthUnitIds,
    filters.value.scheduleIds,
    filters.value.shiftIds
  )
)

/** Cargas independentes: uma falha não impede as outras (padrão de Pagamentos prioritários). */
async function loadOptions(): Promise<void> {
  await Promise.all([
    dsAttendanceStore.filterHealthUnits.length
      ? Promise.resolve()
      : dsAttendanceStore.fetchFilterOptions(),
    new FieldOptionsService()
      .getOperationalGroups()
      .then((groups) => {
        groupOptions.value = groups.map((group) => ({ label: group.name, value: group.name }))
      })
      .catch((error: unknown) =>
        notifyError(error, 'Não foi possível carregar os grupos operacionais')
      ),
    // `GET /shifts` pode responder 403 ao receptor puro: o filtro de Turno avisa e segue.
    Network.shift
      .list()
      .then((response) => {
        shifts.value = response.data ?? []
      })
      .catch(() => {
        shiftOptionsUnavailable.value = true
      })
  ])
}

/* ------------------------------------------------------------ lista e contadores */

const items = ref<ActivityRequestItem[]>([])
const count = ref(0)
const counters = ref<ModuleViewCounters | null>(null)
const serverNow = ref<string | null>(null)
const loading = ref(true)
const loadError = ref<string | null>(null)

const { nowMs } = useServerNowTick(() => serverNow.value)

const pageCount = computed(() => Math.max(1, Math.ceil(count.value / ACTIVITY_REQUEST_PAGE_SIZE)))

let loadSeq = 0

/**
 * "Reabertas" é quick view (`view=ALL&reopened=true`), não filtro: como as outras, ela não
 * pode recortar os contadores — senão, ao clicá-la, Vencidas/No prazo/Todas passariam a
 * contar só as reabertas (M1-B, balão 1: o número respeita os DEMAIS filtros).
 */
function countersQueryOf(current: ActivityRequestQuery): ActivityRequestQuery {
  return activeQuickView.value === 'REOPENED' ? { ...current, reopened: null } : current
}

async function load(): Promise<void> {
  const seq = ++loadSeq
  const current = query.value
  loading.value = true
  loadError.value = null
  try {
    const [list, countersResponse] = await Promise.all([
      Network.dsAttendance.listActivityRequests(current),
      // Contador é conveniência: a falha dele apaga os números ("—"), nunca a lista.
      Network.dsAttendance.getActivityRequestCounters(countersQueryOf(current)).catch(() => null)
    ])
    if (seq !== loadSeq) return
    items.value = list.items
    count.value = list.count
    serverNow.value = list.serverNow
    counters.value = countersResponse
    if (countersResponse) emit('counters', countersResponse)
    openDrawerFromSearch()
  } catch (error) {
    if (seq !== loadSeq) return
    items.value = []
    count.value = 0
    loadError.value = getApiErrorMessage(
      error,
      'Não foi possível carregar as solicitações de Registro de Atividade.'
    )
  } finally {
    if (seq === loadSeq) loading.value = false
  }
}

watch(query, () => void load(), { deep: true })

/* ------------------------------------------------------------ gaveta e navegação */

const isDrawerOpen = ref(false)
const drawerItem = ref<ActivityRequestItem | null>(null)

function openDrawerById(entityId: number): void {
  const item = items.value.find((candidate) => candidate.entityId === entityId)
  if (!item) return
  drawerItem.value = item
  isDrawerOpen.value = true
}

/** `#1287` na busca abre a gaveta direto (M1-B, balão 1). */
function openDrawerFromSearch(): void {
  const match = /^#(\d+)$/.exec(filters.value.search.trim())
  if (match) openDrawerById(Number(match[1]))
}

function openRecord(entityId: number): void {
  const item = items.value.find((candidate) => candidate.entityId === entityId) ?? drawerItem.value
  if (!item) return
  void openContainer(entityId, item.payload.status)
}

/* ------------------------------------------------------------ export */

const isExportConfirmOpen = ref(false)
const isExporting = ref(false)

async function confirmExport(): Promise<void> {
  isExporting.value = true
  try {
    await Network.dsAttendance.exportActivityRequests(query.value)
    toast.success('Exportação concluída.')
    isExportConfirmOpen.value = false
  } catch (error) {
    notifyError(error, 'Não foi possível exportar as solicitações.')
  } finally {
    isExporting.value = false
  }
}

/* ------------------------------------------------------------ estado vazio */

const emptyTitle = computed(() => {
  if (hasActive.value) return 'Nenhum resultado para estes filtros'
  if (filters.value.view === 'OVERDUE') return 'Nenhuma solicitação vencida — tudo em dia'
  return 'Nenhuma solicitação neste recorte'
})

onMounted(() => {
  void loadOptions()
  void load()
})

return (_ctx: any,_cache: any) => {
  const _component_v_pagination = _resolveComponent("v-pagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(savedViews).views.value, (view) => {
        return (_openBlock(), _createElementBlock("span", {
          key: view.id,
          class: "inline-flex items-center gap-1 rounded-full border border-lightGray30 bg-white px-2 py-0.5 text-xs"
        }, [
          _createElementVNode("button", {
            type: "button",
            "data-testid": `activity-requests-saved-view-${view.id}`,
            onClick: ($event: any) => (applySavedView(view))
          }, [
            (view.isDefault)
              ? (_openBlock(), _createElementBlock("span", _hoisted_5, "★ "))
              : _createCommentVNode("", true),
            _createTextVNode(_toDisplayString(view.name), 1)
          ], 8, _hoisted_4),
          _createElementVNode("button", {
            type: "button",
            class: "text-neutral50",
            "aria-label": `Remover a visão ${view.name}`,
            "data-testid": `activity-requests-saved-view-${view.id}-remove`,
            onClick: ($event: any) => (_unref(savedViews).removeView(view.id))
          }, " × ", 8, _hoisted_6)
        ]))
      }), 128)),
      _createElementVNode("button", {
        type: "button",
        class: "text-xs font-bold text-primary30 underline",
        "data-testid": "activity-requests-save-view-button",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (isSaveFormOpen.value = !isSaveFormOpen.value))
      }, " + Salvar visão "),
      _createElementVNode("div", _hoisted_7, [
        _createVNode(SelectFilter, {
          label: "Ordenar por",
          "is-open": isSortOpen.value,
          options: _unref(SORT_OPTIONS),
          "selected-value": _unref(filters).orderBy,
          "dropdown-width": "w-64",
          "align-right": "",
          "data-testid": "activity-requests-sort",
          onToggle: _cache[1] || (_cache[1] = ($event: any) => (isSortOpen.value = !isSortOpen.value)),
          onSelect: onSort
        }, null, 8, ["is-open", "options", "selected-value"]),
        (_ctx.canExport)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createVNode(OutlinedButton, {
                text: "Exportar",
                width: "auto",
                height: "32px",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (isExportConfirmOpen.value = true))
              })
            ]))
          : _createCommentVNode("", true)
      ]),
      (isSaveFormOpen.value)
        ? (_openBlock(), _createElementBlock("form", {
            key: 0,
            class: "flex w-full flex-wrap items-center gap-2 rounded-lg border border-lightGray30 bg-white p-2",
            "data-testid": "activity-requests-save-view-form",
            onSubmit: _withModifiers(submitSaveView, ["prevent"])
          }, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newViewName).value = $event)),
              maxlength: _unref(SAVED_VIEW_NAME_MAX_LENGTH),
              placeholder: "Nome da visão",
              class: "h-8 rounded-md border border-lightGray30 px-2 text-sm",
              "data-testid": "activity-requests-save-view-name-input"
            }, null, 8, _hoisted_10), [
              [_vModelText, newViewName.value]
            ]),
            _createElementVNode("label", _hoisted_11, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((newViewIsDefault).value = $event)),
                type: "checkbox",
                "data-testid": "activity-requests-save-view-default-checkbox"
              }, null, 512), [
                [_vModelCheckbox, newViewIsDefault.value]
              ]),
              _createTextVNode(" Abrir esta visão por padrão nesta aba ")
            ]),
            _hoisted_12,
            _hoisted_13
          ], 40, _hoisted_9))
        : _createCommentVNode("", true)
    ]),
    _createVNode(ActivityRequestQuickViews, {
      counters: counters.value,
      active: activeQuickView.value,
      loading: loading.value && counters.value === null,
      onSelect: onQuickView
    }, null, 8, ["counters", "active", "loading"]),
    _createVNode(ActivityRequestFilterBar, {
      filters: _unref(filters),
      "unit-options": unitOptions.value,
      "schedule-options": scheduleOptions.value,
      "shift-options": shiftOptions.value,
      "group-options": groupOptions.value,
      "shift-options-unavailable": shiftOptionsUnavailable.value,
      onUpdate: _unref(update),
      onClear: _unref(clear)
    }, null, 8, ["filters", "unit-options", "schedule-options", "shift-options", "group-options", "shift-options-unavailable", "onUpdate", "onClear"]),
    (loading.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, _renderList(3, (n) => {
          return _createElementVNode("div", {
            key: n,
            class: "mb-4 h-40 animate-pulse rounded-lg bg-lightGray30"
          })
        }), 64))
      : (loadError.value)
        ? (_openBlock(), _createBlock(ReportErrorState, {
            key: 1,
            title: "Não foi possível carregar as solicitações",
            message: loadError.value,
            onRetry: _cache[5] || (_cache[5] = ($event: any) => (load()))
          }, null, 8, ["message"]))
        : (items.value.length)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item) => {
                return (_openBlock(), _createBlock(ActivityRequestCard, {
                  key: item.id,
                  item: item,
                  "now-ms": _unref(nowMs),
                  onDetails: openDrawerById,
                  onSendRecord: openRecord
                }, null, 8, ["item", "now-ms"]))
              }), 128)),
              (pageCount.value > 1)
                ? (_openBlock(), _createBlock(_component_v_pagination, {
                    key: 0,
                    "model-value": _unref(page),
                    length: pageCount.value,
                    "total-visible": 5,
                    density: "comfortable",
                    color: "primary",
                    "onUpdate:modelValue": _unref(setPage)
                  }, null, 8, ["model-value", "length", "onUpdate:modelValue"]))
                : _createCommentVNode("", true)
            ], 64))
          : (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createElementVNode("p", _hoisted_15, _toDisplayString(emptyTitle.value), 1),
              (hasActive.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "text-sm font-bold text-primary30 underline",
                    "data-testid": "activity-requests-empty-clear",
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (_unref(clear)()))
                  }, " Limpar filtros "))
                : _createCommentVNode("", true)
            ])),
    _createVNode(Modal, {
      "modal-active": isExportConfirmOpen.value,
      onClose: _cache[8] || (_cache[8] = ($event: any) => (isExportConfirmOpen.value = false))
    }, {
      header: _withCtx(() => [
        _hoisted_16
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_18, [
          _createElementVNode("button", {
            type: "button",
            class: "text-sm",
            "data-testid": "activity-requests-export-cancel",
            onClick: _cache[7] || (_cache[7] = ($event: any) => (isExportConfirmOpen.value = false))
          }, " Cancelar "),
          _createElementVNode("button", {
            type: "button",
            class: "rounded-md bg-primary30 px-3 py-1 text-sm font-bold text-white disabled:opacity-50",
            disabled: isExporting.value || count.value === 0,
            "data-testid": "activity-requests-export-submit",
            onClick: confirmExport
          }, " Exportar ", 8, _hoisted_19)
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("p", _hoisted_17, _toDisplayString(count.value === 1
            ? '1 linha será exportada'
            : `${count.value.toLocaleString('pt-BR')} linhas serão exportadas`) + " com os filtros atuais (acima de 20.000 o servidor recusa — refine o recorte). ", 1)
      ]),
      _: 1
    }, 8, ["modal-active"]),
    _createVNode(ActivityRequestDetailDrawer, {
      modelValue: isDrawerOpen.value,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((isDrawerOpen).value = $event)),
      item: drawerItem.value,
      "now-ms": _unref(nowMs),
      onSendRecord: openRecord
    }, null, 8, ["modelValue", "item", "now-ms"])
  ]))
}
}

})